import { addSeconds, max } from "date-fns";

import Route, {
  RoutePreview,
  RoutePreviewStop,
  RouteStop,
} from "@mapmycustomers/shared/types/entity/Route";

import isValidDate from "@app/util/isValidDate";
import { parseApiDate } from "@app/util/parsers";
import { DEFAULT_ALLOTTED_TIME_SECONDS } from "@app/util/route/constants";
import getRemainingRouteStops from "@app/util/route/getRemainingRouteStops";
import displayOrderComparator from "@app/util/sorting/displayOrderComparator";

import routeStopToPreviewStop from "../store/routeDetails/utils/routeStopToPreviewStop";

import hasRouteEndLocation from "./hasRouteEndLocation";
import isStoredRoutePreviewCurrent from "./isStoredRoutePreviewCurrent";

// Departure from a stop, mirroring the BE: a pinned stop cannot begin service before its
// scheduledTime, so it waits when early; a late arrival cannot rewind.
//
// An unparseable pin carries no constraint, and is deliberately NOT a reason to fall back to a
// preview the way an unparseable startAt is: `routing-helper` drops such a pin the same way, so a
// real solve would return these exact arrival times. (No start time means no clock at all.)
const getDepartureTime = (
  arrival: Date,
  allottedTime: number,
  scheduledTime: RouteStop["scheduledTime"]
): Date => {
  const scheduled = scheduledTime ? parseApiDate(scheduledTime) : undefined;
  const serviceStart = scheduled && isValidDate(scheduled) ? max([scheduled, arrival]) : arrival;
  return addSeconds(serviceStart, allottedTime);
};

/**
 * Rebuild the `RoutePreview` the scene consumes out of the metrics already persisted on the
 * route, so opening a route whose stored solve is still current costs no Google call.
 *
 * Everything but the arrival times comes straight off the route: the polyline and totals from
 * `routeDetail`, the per-stop travel legs from the `travelTime`/`travelDistance` the BE projects
 * onto each stop from `routeDetail.legMetrics`. Arrival times are deliberately NOT persisted —
 * they are ours, not Google's, and are derived here from the start time plus accumulated travel
 * and allotted time exactly as `routing-helper` derives them.
 *
 * Returns undefined whenever the stored solve cannot be trusted or cannot be rendered, in which
 * case the caller falls back to buying a preview.
 */
const buildPreviewFromStoredRoute = (route: Route): RoutePreview | undefined => {
  if (!isStoredRoutePreviewCurrent(route)) {
    return undefined;
  }

  const { routeDetail } = route;
  const startAt = parseApiDate(routeDetail.startAt);
  if (!isValidDate(startAt)) {
    return undefined;
  }

  // The BE projects legMetrics[i] onto the i-th stop in displayOrder, so the running clock has to
  // walk that same order to mean anything. Its reads already emit them sorted; this is insurance.
  //
  // Checked-off stops are dropped, not walked: a stored solve that still describes this drive was
  // computed without them (isStoredRoutePreviewCurrent only accepts one whose checked-off stops
  // are bare), so they carry no leg to accumulate and the totals already exclude their driving.
  const stops = getRemainingRouteStops(route).toSorted(displayOrderComparator);

  let currentTime = startAt;
  let stopsTravelTime = 0;
  let stopsTravelDistance = 0;
  const previewStops: RoutePreviewStop[] = [];

  for (const [index, stop] of stops.entries()) {
    // Guaranteed present by isStoredRoutePreviewCurrent; the ?? 0 only satisfies the compiler.
    const travelTime = stop.travelTime ?? 0;
    const travelDistance = stop.travelDistance ?? 0;
    const previewStop = routeStopToPreviewStop(stop, index + 1);
    // A stop with no coordinates cannot be placed on the map. A real preview would have
    // reported it, so hand the open back to the preview path rather than dropping the stop.
    if (!previewStop) {
      return undefined;
    }

    const arrival = addSeconds(currentTime, travelTime);
    previewStops.push({
      ...previewStop,
      arrivalTime: arrival.toISOString(),
      // Indexes the preview request, which excludes checked-off stops — so this is the position
      // within the remaining stops, which is exactly what we are walking.
      originalIndex: index,
      travelDistance,
      travelTime,
    });

    stopsTravelTime += travelTime;
    stopsTravelDistance += travelDistance;
    currentTime = getDepartureTime(
      arrival,
      // Per-stop value wins, else the route default, else the fixed backstop (`??` keeps an
      // explicit 0).
      stop.allottedTime ?? routeDetail.allottedTime ?? DEFAULT_ALLOTTED_TIME_SECONDS,
      stop.scheduledTime
    );
  }

  const hasEnd = hasRouteEndLocation(routeDetail);
  // Subtraction is the only way back to the final leg here: the BE projects a stored leg array
  // onto the stops but never re-emits its trailing end-location entry, so the read path has to
  // recover it from the totals — the same derivation getRouteEndingLocationDetails renders with.
  const endTravelTime = (routeDetail.totalDuration ?? 0) - stopsTravelTime;
  const endTravelDistance = (routeDetail.totalDistance ?? 0) - stopsTravelDistance;

  return {
    endAddress: routeDetail.endAddress,
    endEntity: routeDetail.endEntity,
    endEntityId: routeDetail.endEntityId,
    endEntityType: routeDetail.endEntityType,
    endGeoPoint: routeDetail.endGeoPoint,
    ...(hasEnd
      ? {
          endLocation: {
            arrivalTime: addSeconds(currentTime, endTravelTime).toISOString(),
            travelDistance: endTravelDistance,
            travelTime: endTravelTime,
          },
        }
      : {}),
    polyline: routeDetail.polyline as string,
    startAddress: routeDetail.startAddress,
    startAt: routeDetail.startAt,
    startEntity: routeDetail.startEntity,
    startEntityId: routeDetail.startEntityId,
    startEntityType: routeDetail.startEntityType,
    startGeoPoint: routeDetail.startGeoPoint,
    stopTime: previewStops,
    totalDistance: routeDetail.totalDistance as number,
    totalDuration: routeDetail.totalDuration as number,
    travelMode: routeDetail.travelMode,
    type: routeDetail.type,
  };
};

export default buildPreviewFromStoredRoute;
